import React from 'react';
import {
  Box,
  Center,
  Text,
  Stack,
  VStack,
  Button,
  useBreakpointValue,
  Flex,
  Icon,
  useBoolean,
} from '@wegroup/design-system';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import IllustrationIconSelector from '../../../../common/src/components/IllustrationIconSelector';
import { chakraCustomScrollBar } from '../../../../common/src/utils/styledComponentsUtils';
import useSessionId from '../../../../flows/src/hooks/useSessionId';
import SmallToolbarWithBack from '../../components/recommendations/SmallToolbarWithBack';
import useRiskAnalysisType from '../../hooks/useRiskAnalysisType';
import useTracking from '../../../../common/src/hooks/tracking/useTracking';
import { useConversation } from '../../../../conversations/src/features/detail/shared/api/getConversationById';
import { usePushNewAdvisoryReport } from '../../../../advisory-report/src/api/createAdvisoryReport';
import { isSocialLoginUrl } from '../../../../app/src/features/authentication/constants/SocialAuth';

const RecommendationsSuccess: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const conversationId = useSessionId();
  const { data: convo } = useConversation(conversationId);
  const partyId = convo?.prospect?.id || '';
  const { t } = useTranslation();
  const isMobile = useBreakpointValue({ base: true, tablet: false });
  const riskAnalysiType = useRiskAnalysisType();
  const isCompanyScan = riskAnalysiType === 'company-scan';
  const { recommendationId } = useParams<{ recommendationId: string }>();
  const [isLoading, setIsLoading] = useBoolean();
  const createNewAdvisortReportForParty = usePushNewAdvisoryReport(partyId);
  const { trackEvent } = useTracking();
  const handleNewAdvisoryReport = async () => {
    setIsLoading.on();
    trackEvent('Create Advisory Report from Risk Analysis');
    await createNewAdvisortReportForParty(
      partyId,
      recommendationId,
      conversationId,
    );
    setIsLoading.off();
  };

  return (
    <Flex
      direction={{ base: 'column', tablet: 'row' }}
      alignItems="stretch"
      h="100%"
      w="100%"
    >
      {!isMobile && (
        <SmallToolbarWithBack
          tooltipText={t('RISK-ANALYSIS_RECOMMENDATIONS_SUCCESS-BUTTON-BACK')}
        />
      )}

      <Box
        flexGrow={1}
        overflowY="auto"
        sx={chakraCustomScrollBar}
        position="relative"
        bg="white"
        py="6"
      >
        <Center h="100%" p="6">
          <VStack spacing="8">
            <Box
              maxW={{ base: '128px', tablet: '256px' }}
              sx={{ svg: { w: '100%', h: '100%' } }}
            >
              <IllustrationIconSelector type="partyhat" />
            </Box>
            <VStack spacing="2" maxW="840px" mx="auto">
              <Text textAlign="center" textStyle="h1Headline">
                {isSocialLoginUrl
                  ? t(
                      'RISK-ANALYSIS_RECOMMENDATIONS_SUCCESS-VIEW_TITLE-CAMPAIGN',
                    )
                  : t(
                      'RISK-ANALYSIS_RECOMMENDATIONS_SUCCESS-VIEW_TITLE-BROKER',
                    )}
              </Text>
              <Text textAlign="center" textStyle="largeBody">
                {isSocialLoginUrl
                  ? t(
                      'RISK-ANALYSIS_RECOMMENDATIONS_SUCCESS-VIEW_BODY-CAMPAIGN',
                    )
                  : isCompanyScan
                  ? t('RISK-ANALYSIS_RECOMMENDATIONS_SUCCESS-COMPANY-SCAN-BODY')
                  : t('RISK-ANALYSIS_RECOMMENDATIONS_SUCCESS-VIEW_BODY-BROKER')}
              </Text>
            </VStack>
            {!isSocialLoginUrl && !isCompanyScan && (
              <Stack direction={{ base: 'column', tabletS: 'row' }} spacing="4">
                <Button
                  as={Link}
                  to={`/conversations/risk-analysis/${conversationId}`}
                  onClick={() => trackEvent('To History from Risk Analysis')}
                  leftIcon={<Icon name="GeneralNavigationUnderwriting" />}
                >
                  {t(
                    'RISK-ANALYSIS_RECOMMENDATIONS_SUCCESS-VIEW_BUTTON-CONVO-HISTORY',
                  )}
                </Button>
                <Button
                  onClick={handleNewAdvisoryReport}
                  leftIcon={<Icon name="GeneralNavigationNewAdvisoryReport" />}
                  isDisabled={isLoading}
                  isLoading={isLoading}
                >
                  {t(
                    'RISK-ANALYSIS_RECOMMENDATIONS_SUCCESS-VIEW_BUTTON-ADVISORY',
                  )}
                </Button>
              </Stack>
            )}
            {!isSocialLoginUrl && isCompanyScan && (
              <Stack direction={{ base: 'column', tabletS: 'row' }} spacing="4">
                <Button
                  as={Link}
                  to={`/dashboard`}
                  variant="secondary"
                  leftIcon={<Icon name="GeneralActionsArrowLeft" />}
                >
                  {t('BACK-TO-DASHBOARD')}
                </Button>
                <Button
                  as={Link}
                  to={`/conversations/company-scan/${conversationId}`}
                  rightIcon={<Icon name="GeneralActionsArrowRight" />}
                >
                  {t('VIEW-CONVERSATION')}
                </Button>
              </Stack>
            )}
          </VStack>
        </Center>
      </Box>
    </Flex>
  );
};

export default RecommendationsSuccess;
